import parseIntOrFloat from '@/utils/parseIntOrFloat'

/** Standardised format for coordinate values with supported converters and validation. */
export default class Coordinate {
  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  /** The latitude for this point. */
  latitude: number
  /** The longitude for this point. */
  longitude: number

  // -------------------------------------------------------------------- //
  // --------------------------- Constructor ---------------------------- //
  // -------------------------------------------------------------------- //

  constructor({
    latitude,
    longitude,
  }: {
    /** The latitude for this point. */
    latitude: number
    /** The longitude for this point. */
    longitude: number
  }) {
    this.latitude = latitude
    this.longitude = longitude
  }

  /**
   * Parses latitude and longitude from an unparsed object and creates a Coordinate instance if both are successfully parsed.
   *
   * @param {Object} obj - An object containing latitude and longitude as strings.
   * @return {Coordinate} A Coordinate instance if both latitude and longitude are successfully parsed, otherwise undefined.
   */
  static fromUnparsedObj(obj: { latitude: string; longitude: string }): Coordinate {
    if (!obj || typeof obj !== 'object') {
      throw new Error('Invalid input: expected an object')
    }

    const parsedLat = parseIntOrFloat(obj.latitude)
    const parsedLng = parseIntOrFloat(obj.longitude)

    if (!parsedLat || !parsedLng) {
      throw new Error('Invalid input: unable to parse latitude or longitude')
    }

    if (parsedLat < -90 || parsedLat > 90) {
      throw new Error(`Invalid latitude value: ${parsedLat} is outside the range [-90, 90]`)
    }

    if (parsedLng < -180 || parsedLng > 180) {
      throw new Error(`Invalid longitude value: ${parsedLng} is outside the range [-180, 180]`)
    }

    return new Coordinate({
      latitude: parsedLat,
      longitude: parsedLng,
    })
  }

  /**
   * Creates a new Coordinate instance from a GeoJSON Point object.
   *
   * @param {GeoJSON.Point} obj - The GeoJSON Point object to convert.
   * @return {Coordinate} The newly created Coordinate instance.
   */
  static fromGEOJsonPoint(obj: GeoJSON.Point): Coordinate {
    // guard clauses
    if (!obj || !obj.coordinates || !Array.isArray(obj.coordinates)) {
      throw new Error('Invalid GeoJSON Point object: missing or invalid coordinates')
    }

    if (obj.coordinates.length < 2 || obj.coordinates.length > 3) {
      throw new Error('Invalid GeoJSON Point object: coordinates array should have 2 or 3 elements')
    }

    const [longitude, latitude] = obj.coordinates

    if (latitude < -90 || latitude > 90) {
      throw new Error(`Invalid latitude value: ${latitude} is outside the range [-90, 90]`)
    }

    if (longitude < -180 || longitude > 180) {
      throw new Error(`Invalid longitude value: ${longitude} is outside the range [-180, 180]`)
    }

    // create coordinate
    return new Coordinate({
      latitude: obj.coordinates[1],
      longitude: obj.coordinates[0],
    })
  }

  /**
   * Creates a new Coordinate instance from an object with Latitude and Longitude properties.
   *
   * @param {Object} obj - An object containing Latitude and Longitude as numbers.
   * @return {Coordinate} The newly created Coordinate instance.
   */
  static fromCapitalizedObj(obj: { Latitude: number; Longitude: number }) {
    // guard clauses
    if (!obj.Latitude || !obj.Longitude)
      throw new Error('Invalid coordinates, missing latitude or longitude')
    if (typeof obj.Latitude !== 'number' || typeof obj.Longitude !== 'number')
      throw new Error('Invalid coordinates, latitude and longitude must be numbers')
    if (obj.Latitude > 90 || obj.Latitude < -90 || obj.Longitude > 180 || obj.Longitude < -180)
      throw new Error('Invalid coordinates, coordinates are outside the bounds of the planet.')

    // create coordinate
    return new Coordinate({
      latitude: obj.Latitude,
      longitude: obj.Longitude,
    })
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Getters ----------------------------- //
  // -------------------------------------------------------------------- //

  /**
   * Tests if the coordinate is with in the bounds of earth and not the default null island.
   *
   * @returns true if is a valid coordinate.
   */
  public get isValid(): boolean {
    if (this.latitude === 0 && this.longitude === 0) return false // null island is a default not a drivable location.
    if (
      this.latitude > 180 ||
      this.latitude < -180 ||
      this.longitude > 180 ||
      this.longitude < -180
    )
      return false // coordinate is outside the bounds of the planet.
    return true
  }

  /**
   * Converts to leaflet supported [lat,lng] format.
   *
   * @returns a latitude longitude point array
   */
  public get asLatLng(): [number, number] {
    return [this.latitude, this.longitude]
  }

  /**
   * Converts to haversine supported [lng,lat] format.
   *
   * @returns a longitude latitude point array
   */
  public get asLngLat(): [number, number] {
    return [this.longitude, this.latitude]
  }

  /**
   * Converts to Valhalla supported lat lon obj format.
   *
   * @returns an obj containing a lat and lon properties.
   */
  public get asAbbreviatedObj(): { lat: number; lon: number } {
    return { lat: this.latitude, lon: this.longitude }
  }

  /**
   * Converts to EV Nav supported Latitude Longitude obj format.
   *
   * @returns an obj containing a Latitude and Longitude properties.
   */
  public get asCapitalizedObj(): { Latitude: number; Longitude: number } {
    return { Latitude: this.latitude, Longitude: this.longitude }
  }
}
